import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Loader2, TrendingUp, Clock, CheckCircle, MessageSquare } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

type DateRange = "7d" | "30d" | "90d";

const FEEDBACK_TYPE_LABELS: Record<string, string> = {
  incorrect_answer: "Incorrect Answer",
  unclear_question: "Unclear Question",
  typo: "Typo",
  outdated_info: "Outdated Info",
  missing_explanation: "Missing Explanation",
  other: "Other",
};

const COLORS = ["hsl(var(--primary))", "hsl(var(--secondary))", "hsl(var(--accent))", "hsl(var(--muted))", "hsl(var(--destructive))", "hsl(var(--chart-5))"];

export function FeedbackAnalyticsDashboard() {
  const [dateRange, setDateRange] = useState<DateRange>("30d");

  const getDateRangeDays = (range: DateRange) => {
    switch (range) {
      case "7d": return 7;
      case "30d": return 30;
      case "90d": return 90;
    }
  };

  const { data: analytics, isLoading } = useQuery({
    queryKey: ["feedback-analytics", dateRange],
    queryFn: async () => {
      const days = getDateRangeDays(dateRange);
      const startDate = startOfDay(subDays(new Date(), days));
      const endDate = endOfDay(new Date());

      // Total submissions
      const { count: totalSubmissions } = await supabase
        .from("mcq_user_feedback")
        .select("*", { count: "exact", head: true })
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      // Resolution time (avg days to review)
      const { data: resolvedFeedback } = await supabase
        .from("mcq_user_feedback")
        .select("created_at, reviewed_at")
        .not("reviewed_at", "is", null)
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const avgResolutionTime = resolvedFeedback?.reduce((acc, f) => {
        const diff = new Date(f.reviewed_at!).getTime() - new Date(f.created_at).getTime();
        return acc + diff / (1000 * 60 * 60 * 24); // Convert to days
      }, 0) / (resolvedFeedback?.length || 1);

      // Acceptance rate by type
      const { data: feedbackByType } = await supabase
        .from("mcq_user_feedback")
        .select("feedback_type, status")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const typeStats = feedbackByType?.reduce((acc, f) => {
        if (!acc[f.feedback_type]) {
          acc[f.feedback_type] = { total: 0, accepted: 0, resolved: 0 };
        }
        acc[f.feedback_type].total++;
        if (f.status === "accepted") acc[f.feedback_type].accepted++;
        if (f.status === "resolved") acc[f.feedback_type].resolved++;
        return acc;
      }, {} as Record<string, { total: number; accepted: number; resolved: number }>);

      const acceptanceByType = Object.entries(typeStats || {}).map(([type, stats]) => ({
        name: FEEDBACK_TYPE_LABELS[type] || type,
        rate: Math.round(((stats.accepted + stats.resolved) / stats.total) * 100),
        total: stats.total,
      }));

      // Top 10 most reported MCQs
      const { data: topMCQs } = await supabase
        .from("mcq_user_feedback")
        .select("mcq_id, mcqs(stem)")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const mcqCounts = topMCQs?.reduce((acc, f) => {
        if (!f.mcq_id) return acc;
        if (!acc[f.mcq_id]) {
          acc[f.mcq_id] = { count: 0, stem: (f.mcqs as any)?.stem || "Unknown" };
        }
        acc[f.mcq_id].count++;
        return acc;
      }, {} as Record<string, { count: number; stem: string }>);

      const topReported = Object.entries(mcqCounts || {})
        .map(([id, data]) => ({ id, ...data }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 10);

      // Volume by user type
      const { data: volumeByType } = await supabase
        .from("mcq_user_feedback")
        .select("is_paid_user")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString());

      const paidCount = volumeByType?.filter(f => f.is_paid_user).length || 0;
      const freeCount = volumeByType?.filter(f => !f.is_paid_user).length || 0;

      const volumeData = [
        { name: "Paid Users", value: paidCount },
        { name: "Free Users", value: freeCount },
      ];

      // Weekly trend
      const { data: weeklyData } = await supabase
        .from("mcq_user_feedback")
        .select("created_at")
        .gte("created_at", startDate.toISOString())
        .lte("created_at", endDate.toISOString())
        .order("created_at", { ascending: true });

      const weeklyTrend = weeklyData?.reduce((acc, f) => {
        const week = format(new Date(f.created_at), "MMM dd");
        if (!acc[week]) acc[week] = 0;
        acc[week]++;
        return acc;
      }, {} as Record<string, number>);

      const trendData = Object.entries(weeklyTrend || {}).map(([date, count]) => ({
        date,
        count,
      }));

      return {
        totalSubmissions: totalSubmissions || 0,
        avgResolutionTime: avgResolutionTime || 0,
        acceptanceByType,
        topReported,
        volumeData,
        trendData,
      };
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Feedback Analytics</h2>
          <p className="text-sm text-muted-foreground">Insights into user feedback patterns and resolution</p>
        </div>
        <Select value={dateRange} onValueChange={(v) => setDateRange(v as DateRange)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Submissions</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.totalSubmissions}</div>
            <p className="text-xs text-muted-foreground">In selected period</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg. Resolution Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{analytics?.avgResolutionTime.toFixed(1)} days</div>
            <p className="text-xs text-muted-foreground">Time to review</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Acceptance Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analytics?.acceptanceByType.reduce((acc, t) => acc + t.rate, 0) / (analytics?.acceptanceByType.length || 1) || 0}%
            </div>
            <p className="text-xs text-muted-foreground">Overall acceptance</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Submission Trend</CardTitle>
            <CardDescription>Daily feedback submissions over time</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={analytics?.trendData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Acceptance Rate by Type</CardTitle>
            <CardDescription>Resolution success by feedback category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics?.acceptanceByType}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="rate" fill="hsl(var(--primary))" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Volume by User Type</CardTitle>
            <CardDescription>Feedback distribution across user segments</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={analytics?.volumeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => `${entry.name}: ${entry.value}`}
                  outerRadius={80}
                  fill="hsl(var(--primary))"
                  dataKey="value"
                >
                  {analytics?.volumeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Top 10 Most Reported MCQs</CardTitle>
            <CardDescription>Questions receiving the most feedback</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analytics?.topReported.map((mcq, idx) => (
                <div key={mcq.id} className="flex items-center justify-between border-b pb-2">
                  <div className="flex-1">
                    <span className="text-sm font-medium">#{idx + 1}</span>
                    <p className="text-sm text-muted-foreground line-clamp-1">{mcq.stem}</p>
                  </div>
                  <span className="text-sm font-bold">{mcq.count} reports</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
